
import { computed, defineComponent, h, nextTick, onMounted, reactive, toRefs } from 'vue';
import { SearchOutline as SearchIcon, ArrowBack as BackIcon } from '@vicons/ionicons5';
import { FormResponse, getReadableFormDates } from '@/types/response';
import responseService from '@/services/response-service';
import ResponseActions from '@/components/responses/ResponseActions.vue';
import ConfirmResponseUnlockModal from '@/components/responses/ConfirmResponseUnlockModal.vue';
import ConfirmSurveyResend from '@/components/responses/ConfirmSurveyResend.vue';
import { fixPagination } from '@/helpers/pagination-helper';
import router from '@/router';

export default defineComponent({
  name: 'Responses',
  components: { ConfirmResponseUnlockModal, SearchIcon, ConfirmSurveyResend, BackIcon },
  props: {
    surveyId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const state = reactive({
      responsesLoaded: false,
      searchQuery: '',
      filteredData: [] as FormResponse[],
      originalData: [] as FormResponse[],
      selectedResponse: null as FormResponse | null,
      showUnlockModal: false,
      showResendModal: false
    });

    const pagination = {
      pageSize: 12
    };

    const updateResponsesInList = async (response: FormResponse): Promise<void> => {
      state.originalData = state.originalData.map((r) => (r.id !== response.id ? r : response));
      state.filteredData = state.filteredData.map((r) => (r.id !== response.id ? r : response));
    };

    const showUnlockModal = async (response: FormResponse): Promise<void> => {
      state.selectedResponse = response;
      state.showUnlockModal = true;
    };

    const showResendModal = async (response: FormResponse): Promise<void> => {
      state.selectedResponse = response;
      state.showResendModal = true;
    };

    const columns = [
      {
        title: 'Email',
        key: 'email'
      },
      {
        title: 'Sent On',
        key: 'sentOn',
        render: (row: FormResponse) => getReadableFormDates(row).sentOn
      },
      {
        title: 'Viewed On',
        key: 'viewedOn',
        render: (row: FormResponse) => getReadableFormDates(row).viewedOn
      },
      {
        title: 'Completed On',
        key: 'completedOn',
        render: (row: FormResponse) => getReadableFormDates(row).completedOn
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (row: FormResponse) =>
          h(ResponseActions, {
            response: row,
            onUnlock: () => showUnlockModal(row),
            onResend: () => showResendModal(row)
          })
      }
    ];

    const loadResponses = async () => {
      const responses = await responseService.getAll(props.surveyId);
      state.originalData = responses;
      state.filteredData = responses;
      state.responsesLoaded = true;
    };

    const navigateToGraph = () => {
      router.push(`/admin/survey/${props.surveyId}/results`);
    };

    const filteredData = computed(() => {
      const searchQuery = state.searchQuery.toLowerCase().trim();
      return state.originalData.filter((response: FormResponse) => {
        const readable = getReadableFormDates(response);
        const toSearch = [response.email, readable.sentOn, readable.viewedOn, readable.completedOn];
        return toSearch.some((value) => value && value.toLowerCase().includes(searchQuery));
      });
    });

    onMounted(async () => {
      await loadResponses();
      await nextTick();
      await fixPagination();
    });

    return { ...toRefs(state), pagination, columns, filteredData, updateResponsesInList, navigateToGraph };
  }
});
